import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Card,
  InputAdornment
} from '@mui/material';
import { Search, DriveEta, Badge } from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getLegacyTasks } from '../api/alfresco';
import { getUserInfo } from '../utils/auth';

const WorkflowHistory = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCompletedTasks();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      setFilteredTasks(
        tasks.filter((task) => {
          const props = task.properties || {};
          return (
            task.description?.toLowerCase().includes(searchLower) ||
            task.title?.toLowerCase().includes(searchLower) ||
            task.workflowInstance?.message?.toLowerCase().includes(searchLower) ||
            props.biat_nom?.toLowerCase().includes(searchLower) ||
            props.biat_prenom?.toLowerCase().includes(searchLower) ||
            props.biat_immatriculation?.toLowerCase().includes(searchLower) ||
            props.biat_marque?.toLowerCase().includes(searchLower) ||
            props.biat_modele?.toLowerCase().includes(searchLower)
          );
        })
      );
    } else {
      setFilteredTasks(tasks);
    }
  }, [searchTerm, tasks]);

  const loadCompletedTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const userInfo = getUserInfo();
      const username = userInfo?.id || userInfo?.userName;
      
      const tasksData = await getLegacyTasks({
        authority: username,
        properties: ['bpm_priority', 'bpm_status', 'bpm_dueDate', 'bpm_description', 'biat_nom', 'biat_prenom', 'biat_immatriculation', 'biat_marque', 'biat_modele'],
        exclude: 'wcmwf:*',
        skipCount: 0,
        maxItems: 50,
        state: 'COMPLETED',
        pooledTasks: false
      });
      
      setTasks(tasksData);
      setFilteredTasks(tasksData);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors du chargement de l\'historique'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTask = (taskId) => {
    navigate(`/task/${taskId}`);
  };

  const formatDate = (dateString, fallback = '(Aucun)', withTime = false) => {
    if (!dateString) return fallback;
    try {
      const date = new Date(dateString);
      const pattern = withTime ? 'dd/MM/yyyy HH:mm' : 'dd MMMM, yyyy';
      return format(date, pattern, { locale: fr });
    } catch {
      return fallback;
    }
  };

  const getOutcomeColor = (outcome) => {
    if (!outcome) return 'default';
    const outcomeLower = outcome.toLowerCase();
    if (outcomeLower.includes('valider') || outcomeLower.includes('terminer')) return 'success';
    if (outcomeLower.includes('incomplet') || outcomeLower.includes('rectifier')) return 'warning';
    return 'default';
  };

  // Fonctions helper pour les informations métier BIAT
  const getClientName = (task) => {
    const props = task.properties || {};
    const nom = props.biat_nom || '';
    const prenom = props.biat_prenom || '';
    const fullName = `${prenom} ${nom}`.trim();
    return fullName || '—';
  };

  const getVehicleInfo = (task) => {
    const props = task.properties || {};
    const immatriculation = props.biat_immatriculation || '';
    const marque = props.biat_marque || '';
    const modele = props.biat_modele || '';
    
    const parts = [];
    if (immatriculation) parts.push(immatriculation);
    if (marque || modele) {
      const vehicule = `${marque} ${modele}`.trim();
      if (vehicule) parts.push(vehicule);
    }
    
    return parts.length > 0 ? parts.join(' - ') : '—';
  };

  const renderTaskDetails = (task) => {
    const clientName = getClientName(task);
    const vehicleInfo = getVehicleInfo(task);
    
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'primary.main' }}>
          {task.description || task.properties?.bpm_description || task.title || task.name}
        </Typography>
        
        {/* Informations métier BIAT */}
        {(clientName !== '—' || vehicleInfo !== '—') && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0.5,
            p: 1,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            mt: 0.5
          }}>
            {clientName !== '—' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Badge fontSize="small" color="primary" sx={{ fontSize: '14px' }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Client:</strong> {clientName}
                </Typography>
              </Box>
            )}
            {vehicleInfo !== '—' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DriveEta fontSize="small" color="primary" sx={{ fontSize: '14px' }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Véhicule:</strong> {vehicleInfo}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Type:</strong> {task.title || task.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Terminé le:</strong> {formatDate(task.workflowInstance?.endDate, '—', true)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Démarré le:</strong> {formatDate(task.workflowInstance?.startDate, '—', true)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Initié par:</strong> {task.workflowInstance?.initiator?.firstName || task.workflowInstance?.initiator?.userName || '—'}
          </Typography>
        </Box>
        {task.outcome && (
          <Chip
            label={`Résultat: ${task.outcome}`}
            color={getOutcomeColor(task.outcome)}
            size="small"
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
          Historique des tâches
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Liste des tâches terminées
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Rechercher par client, véhicule, immatriculation, description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button 
            variant="contained" 
            startIcon={<Search />}
            color="primary"
          >
            Rechercher
          </Button>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(6, 95, 70, 0.08)' }}>
                <TableCell sx={{ fontWeight: 600, color: 'primary.main', minWidth: 400 }}>
                  Tâche
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Assigné à
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'Aucune tâche trouvée' : 'Aucune tâche terminée'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => (
                  <TableRow 
                    key={task.id} 
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(6, 95, 70, 0.05)' 
                      } 
                    }}
                  >
                    <TableCell>{renderTaskDetails(task)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {task.owner?.userName || task.assignee ? task.owner?.userName || task.assignee : 'Non assignée'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenTask(task.id)}
                        color="primary"
                      >
                        Voir détails
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </Card>
    </Container>
  );
};

export default WorkflowHistory;

